/**
 * Global Error Handler Component
 * Displays centralized error toasts from the global error store
 * Supports multiple parallel errors, server-down banners, and auto-dismissal
 */

import React from 'react';
import { useErrorToastStore } from '../stores/errorToastStore';
import type { ErrorToast } from '../stores/errorToastStore';

const GlobalErrorHandler: React.FC = () => {
  const { toasts, serverDownDetected, removeError, clearServerDownErrors } =
    useErrorToastStore();

  const getToastStyles = (type: ErrorToast['type']) => {
    switch (type) {
      case 'error':
        return 'bg-red-50 border-red-300 text-red-900 dark:bg-red-900/20 dark:border-red-700 dark:text-red-200';
      case 'warning':
        return 'bg-orange-50 border-orange-300 text-orange-900 dark:bg-orange-900/20 dark:border-orange-700 dark:text-orange-200';
      case 'network':
        return 'bg-yellow-50 border-yellow-300 text-yellow-900 dark:bg-yellow-900/20 dark:border-yellow-700 dark:text-yellow-200';
      case 'server-down':
        return 'bg-red-600 border-red-700 text-white dark:bg-red-800 dark:border-red-900';
      default:
        return 'bg-gray-50 border-gray-300 text-gray-900 dark:bg-gray-900/20 dark:border-gray-700 dark:text-gray-200';
    }
  };

  const getIcon = (type: ErrorToast['type']) => {
    switch (type) {
      case 'error':
      case 'server-down':
        return 'ri-error-warning-line';
      case 'warning':
        return 'ri-alert-line';
      case 'network':
        return 'ri-wifi-off-line';
      default:
        return 'ri-information-line';
    }
  };

  const getIconColor = (type: ErrorToast['type']) => {
    switch (type) {
      case 'error':
        return 'text-red-500';
      case 'warning':
        return 'text-orange-500';
      case 'network':
        return 'text-yellow-600';
      case 'server-down':
        return 'text-white';
      default:
        return 'text-gray-500';
    }
  };

  // Separate server-down toasts from regular errors
  const serverDownToasts = toasts.filter((t) => t.type === 'server-down');
  const regularToasts = toasts.filter((t) => t.type !== 'server-down');

  return (
    <>
      {/* Server Down Banner - Fixed at top, full width */}
      {serverDownDetected && serverDownToasts.length > 0 && (
        <div className="fixed top-0 left-0 right-0 z-[9999]">
          {serverDownToasts.map((toast) => (
            <div
              key={toast.id}
              className={`
                flex items-center justify-between px-6 py-2 border-b-2
                animate-in slide-in-from-top duration-300
                ${getToastStyles(toast.type)}
              `}
            >
              <div className="flex items-center flex-1">
                <i className={`${getIcon(toast.type)} mr-4 text-xl flex-shrink-0`}></i>
                <div className="flex-1">
                  <p className="font-semibold text-sm text-base">{toast.message}</p>
                  {toast.metadata?.url && (
                    <p className="text-xs opacity-90 mt-1">
                      Failed request: {toast.metadata.url}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => window.location.reload()}
                  className="ml-4"
                  aria-label="Refresh page"
                  title="Refresh page"
                >
                  <i className="text-white ri-refresh-line text-xl"></i>
                </button>
                <button
                  onClick={() => {
                    removeError(toast.id);
                    clearServerDownErrors();
                  }}
                  className="ml-4"
                  aria-label="Dismiss server error"
                  title="Dismiss"
                >
                  <i className="text-white ri-close-line text-xl"></i>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Regular Error Toasts - Stacked in top-right corner */}
      {regularToasts.length > 0 && (
        <div className="fixed top-4 right-4 z-[9998] space-y-3 max-w-md">
          {regularToasts.map((toast) => (
            <div
              key={toast.id}
              className={`
                flex items-start p-4 rounded-lg border-2 shadow-lg
                animate-in slide-in-from-right-full duration-300
                ${getToastStyles(toast.type)}
              `}
              role="alert"
            >
              <i
                className={`
                  ${getIcon(toast.type)}
                  ${getIconColor(toast.type)}
                  mr-3 text-xl flex-shrink-0 mt-0.5
                `}
              ></i>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold leading-tight break-words">
                  {toast.message}
                </p>
                {toast.metadata && (
                  <div className="mt-2 text-xs opacity-75 space-y-1">
                    {toast.metadata.url && (
                      <p className="truncate" title={toast.metadata.url}>
                        URL: {toast.metadata.url}
                      </p>
                    )}
                    {toast.metadata.status && (
                      <p>Status: {toast.metadata.status}</p>
                    )}
                    {toast.metadata.code && (
                      <p>Code: {toast.metadata.code}</p>
                    )}
                  </div>
                )}
                {/* The raw category is a DEVELOPER label -- 'server-error', 'client-error',
                    'application-error' -- and printing it verbatim put "Server Error"
                    under messages that were nothing of the sort. A tender refusing a
                    second call while the first is open is the rule working; captioning it
                    "Server Error" tells the operator the system is broken and to try
                    again, which is the opposite of what they should do.
                    (See tender-calls.service.js, which threw with no status code so every
                    refusal was classified 5xx.)

                    Only the two categories that actually tell the reader something they
                    can act on are kept, and in words rather than in the internal name.
                    Everything else shows the message alone, which is the part that says
                    what happened. */}
                {(toast.category === 'network' || toast.category === 'server-down') && (
                  <p className="text-xs opacity-60 mt-1">
                    {toast.category === 'network'
                      ? 'Check your connection'
                      : 'The server is unreachable'}
                  </p>
                )}
              </div>
              <button
                onClick={() => removeError(toast.id)}
                className="ml-3 opacity-70 hover:opacity-100 transition-opacity flex-shrink-0"
                aria-label="Dismiss error"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

export default GlobalErrorHandler;
